// EmptyState — centered "nothing here yet" card: icon + primary line +
// secondary line + optional action. Matches the existing patterns
// (PropertiesPage "No properties found", TemplatesTab "No templates yet.").
import React from "react";
import { Box, Card, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

interface EmptyStateProps {
  /** Large muted icon above the title (rendered at 64px, faded). */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional action node (usually a Button — give it `sx={{ mt: 2 }}` handled here). */
  action?: React.ReactNode;
  /** Card look: default elevated app card, or `outlined` (settings-style). */
  variant?: "elevation" | "outlined";
  sx?: SxProps<Theme>;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  variant = "elevation",
  sx,
}) => (
  <Card
    variant={variant}
    sx={[
      { p: variant === "outlined" ? 4 : 6, textAlign: "center" },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {icon && (
      <Box
        sx={{
          mb: 2,
          color: "text.secondary",
          opacity: 0.3,
          "& svg": { fontSize: 64 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography variant="h6" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Card>
);

export default EmptyState;
